import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
export function PlatformHero() {
  return <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto overflow-hidden" data-id="element-352">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-353">
        {/* Left Column: Copy */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6,
        ease: 'easeOut'
      }} className="max-w-2xl" data-id="element-354">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-355">
            <span className="w-8 h-px bg-teal" data-id="element-356"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-357">
              Platform Overview
            </span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-[1.1] mb-6 text-balance" data-id="element-358">
            A New Platform for Human Capability, Civic Development, and
            Leadership Readiness
          </h1>

          <p className="text-lg text-ink-muted mb-10 leading-relaxed max-w-xl" data-id="element-359">
            SyNeural's platform architecture brings together CJI and NCJI into a
            unified system for measuring, developing, and strengthening judgment
            across individuals, institutions, and leadership environments.
            Designed to move from assessment to advancement, the platform helps
            translate capability gaps into development pathways, support
            systems, and broader opportunity.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 mb-6" data-id="element-360">
            <button className="bg-navy hover:bg-navy-light text-white font-medium px-6 py-3.5 rounded-md transition-colors shadow-sm flex items-center justify-center gap-2" data-id="element-361">
              Request a Demo
              <ArrowRight size={18} data-id="element-362" />
            </button>
            <button className="bg-transparent border border-navy text-navy hover:bg-surface-muted font-medium px-6 py-3.5 rounded-md transition-colors flex items-center justify-center" data-id="element-363">
              Explore NCJI
            </button>
          </div>
        </motion.div>

        {/* Right Column: Abstract Diagram */}
        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} animate={{
        opacity: 1,
        scale: 1
      }} transition={{
        duration: 0.8,
        delay: 0.2,
        ease: 'easeOut'
      }} className="relative h-[400px] lg:h-[500px] w-full flex items-center justify-center" data-id="element-364">
          <div className="absolute inset-0 bg-gradient-to-tr from-surface-bg via-white to-surface-muted rounded-full blur-3xl opacity-50 -z-10" data-id="element-365"></div>

          <svg viewBox="0 0 500 500" className="w-full h-full max-w-lg" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-366">
            {/* Core Center */}
            <circle cx="250" cy="250" r="40" fill="#123B5D" data-id="element-367" />
            <circle cx="250" cy="250" r="60" stroke="#245B8F" strokeWidth="2" strokeDasharray="4 4" data-id="element-368" />
            <text x="250" y="254" fontSize="12" fill="#FFFFFF" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-369">
              CJI
            </text>

            {/* NCJI Framework Ring */}
            <circle cx="250" cy="250" r="120" stroke="#2E8C88" strokeWidth="1.5" className="opacity-60" data-id="element-370" />
            <path d="M 250 130 A 120 120 0 0 1 370 250" stroke="#2E8C88" strokeWidth="4" data-id="element-371" />

            {/* Connecting Lines */}
            <path d="M 250 210 L 250 130" stroke="#D9E2EC" strokeWidth="1.5" data-id="element-372" />
            <path d="M 290 250 L 370 250" stroke="#D9E2EC" strokeWidth="1.5" data-id="element-373" />
            <path d="M 250 290 L 250 370" stroke="#D9E2EC" strokeWidth="1.5" data-id="element-374" />
            <path d="M 210 250 L 130 250" stroke="#D9E2EC" strokeWidth="1.5" data-id="element-375" />

            {/* Outer Nodes */}
            {/* Human Development */}
            <g transform="translate(250, 130)" data-id="element-376">
              <circle cx="0" cy="0" r="12" fill="#FFFFFF" stroke="#6FAE8A" strokeWidth="2" data-id="element-377" />
              <text x="0" y="-20" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="500" data-id="element-378">
                Human Development
              </text>
            </g>

            {/* Institutional Readiness */}
            <g transform="translate(370, 250)" data-id="element-379">
              <circle cx="0" cy="0" r="14" fill="#FFFFFF" stroke="#245B8F" strokeWidth="2" data-id="element-380" />
              <text x="22" y="4" fontSize="12" fill="#1F2A37" fontFamily="Inter" fontWeight="500" data-id="element-381">
                Institutional Readiness
              </text>
            </g>

            {/* Leadership Qualification */}
            <g transform="translate(250, 370)" data-id="element-382">
              <circle cx="0" cy="0" r="16" fill="#FFFFFF" stroke="#123B5D" strokeWidth="2.5" data-id="element-383" />
              <text x="0" y="30" fontSize="12" fill="#1F2A37" fontFamily="Inter" textAnchor="middle" fontWeight="600" data-id="element-384">
                Leadership Qualification
              </text>
            </g>

            {/* Global Deployment */}
            <g transform="translate(130, 250)" data-id="element-385">
              <circle cx="0" cy="0" r="10" fill="#FFFFFF" stroke="#2E8C88" strokeWidth="2" data-id="element-386" />
              <text x="-20" y="4" fontSize="12" fill="#5B6675" fontFamily="Inter" textAnchor="end" fontWeight="500" data-id="element-387">
                Global Deployment
              </text>
            </g>

            {/* Orbiting Elements */}
            <circle cx="165" cy="165" r="4" fill="#6FAE8A" data-id="element-388" />
            <circle cx="335" cy="335" r="6" fill="#245B8F" data-id="element-389" />
            <circle cx="165" cy="335" r="3" fill="#2E8C88" data-id="element-390" />
          </svg>
        </motion.div>
      </div>
    </section>;
}